/* iter466 — Shared MenuModalView used by NutritionLab + MyMenusPage.
   iter476 — Show PRAL + Calorie Density on day totals + meal estimates.
            Add "Изпрати в Симулатор на чинията" button per meal.
   iter479 — Send-to-plate now actually adds the meal to the food diary via
            /api/meals/add-batch, then navigates to the Diary's Plate
            Simulator section so the user sees the dish analysed. */
import React from "react";
import axios from "axios";
import { X, ChefHat, ChevronRight, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEAL_LABELS = {
  bg: { breakfast: "Закуска", lunch: "Обяд", dinner: "Вечеря", snack: "Снакс" },
  en: { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner", snack: "Snack" },
  es: { breakfast: "Desayuno", lunch: "Almuerzo", dinner: "Cena", snack: "Merienda" },
};

// iter479 — Lightweight kcal/macros heuristic per ingredient name. Matches
// backend `_est_kcal_per_g` so values shown in diary roughly equal AI totals.
function estimateMacrosPer100g(name) {
  const n = (name || "").toLowerCase();
  const m = (kcal, p, c, f, fi = 0) => ({ kcal, p, c, f, fi });
  if (/(зехтин|масло|oil|butter)/.test(n))                                   return m(850, 0, 0, 95);
  if (/(ядк|бадем|орех|лешник|nuts?|almond|walnut|семен|семк|seed)/.test(n)) return m(580, 18, 20, 50, 8);
  if (/(сирене|кашкавал|cheese|пармезан)/.test(n))                            return m(360, 25, 2, 28);
  if (/(бран|трици|bran)/.test(n))                                             return m(216, 16, 65, 4, 43);
  if (/(овес|oat|oatmeal|porridge|каша)/.test(n))                              return m(380, 13, 68, 7, 10);
  if (/(хляб|bread|тост|крекер|тестен|паста|макарон|spaghetti|pasta|пица)/.test(n)) return m(270, 9, 50, 4, 3);
  if (/(ориз|rice)/.test(n))                                                   return m(130, 2.7, 28, 0.3, 0.4);
  if (/(киноа|quinoa|булгур|елда)/.test(n))                                    return m(120, 4.5, 21, 1.9, 2.8);
  if (/(леща|lentil|боб|нахут|chickpea)/.test(n))                              return m(120, 9, 20, 0.5, 8);
  if (/(месо|пиле|пуйк|говежд|свин|телеш|телешк|beef|chicken|pork|turkey|lamb)/.test(n)) return m(170, 25, 0, 8);
  if (/(риба|сьомга|тон|скумрия|сардин|fish|salmon|tuna|mackerel|sardine|халибут|halibut)/.test(n)) return m(160, 22, 0, 8);
  if (/(яйц|egg)/.test(n))                                                     return m(150, 13, 1, 10);
  if (/(кейл|kale|колард|collard)/.test(n))                                    return m(35, 2.9, 7, 0.4, 2.6);
  if (/(спанак|spinach)/.test(n))                                              return m(23, 2.9, 3.6, 0.4, 2.2);
  if (/(броколи|broccoli|карфиол|cauliflower)/.test(n))                        return m(35, 2.5, 7, 0.4, 2.6);
  if (/(домат|tomato)/.test(n))                                                return m(18, 0.9, 3.9, 0.2, 1.2);
  if (/(плод|ябълк|круш|банан|fruit|apple|banana)/.test(n))                    return m(55, 0.5, 13, 0.3, 2.4);
  if (/(ягод|малин|боровинк|berry)/.test(n))                                   return m(45, 1, 11, 0.3, 3);
  if (/(лук|scallion|скалион|чесън|garlic|leek)/.test(n))                      return m(32, 1.8, 7, 0.2, 2.6);
  if (/(йогурт|кисело мляко|yogurt|yoghurt)/.test(n))                          return m(65, 4, 5, 3);
  if (/(мляко|milk)/.test(n))                                                  return m(50, 3.4, 5, 1.5);
  if (/(зеленчук|vegetable)/.test(n))                                          return m(30, 2, 5, 0.3, 2);
  return m(120, 6, 12, 4, 1);
}

export default function MenuModalView({ menu, menuId, lang = "bg", isDark = false, onClose, onMenuUpdate, profileApplied: profileAppliedProp }) {
  const navigate = useNavigate();
  const [recomputing, setRecomputing] = React.useState(false);
  const [profileApplied, setProfileApplied] = React.useState(profileAppliedProp || null);  // iter482
  const days = menu?.days || [];
  const t = MEAL_LABELS[lang] || MEAL_LABELS.bg;
  const title = lang === "bg" ? "AI меню — GPT-5.2" : "AI menu — GPT-5.2";
  const disclaimer = lang === "bg" ? "Educational · НЕ е медицински съвет." : "Educational · Not medical advice.";

  // iter481 — Recompute DIL/NIS with user's metabolic profile
  const recomputeWithProfile = async () => {
    if (!menuId) {
      toast.error(lang === "bg" ? "Не е намерен ID на менюто" : "Menu ID missing");
      return;
    }
    setRecomputing(true);
    try {
      toast.loading(
        lang === "bg" ? "Преизчислявам с твоя профил…" : "Recomputing with your profile…",
        { id: "recompute" }
      );
      const res = await axios.post(`${API}/foodlab/menus/${menuId}/recompute`);
      const updated = res.data?.menu;
      const applied = res.data?.profile_applied || {};
      setProfileApplied(applied);  // iter482
      const summary = [];
      if (applied.phenotype) summary.push(applied.phenotype);
      if (applied.homa_ir) summary.push(`HOMA-IR ${applied.homa_ir}`);
      if (applied.pcos) summary.push("PCOS");
      toast.success(
        lang === "bg"
          ? `Преизчислено${summary.length ? `: ${summary.join(" · ")}` : ""}`
          : `Recomputed${summary.length ? `: ${summary.join(" · ")}` : ""}`,
        { id: "recompute" }
      );
      if (updated && onMenuUpdate) onMenuUpdate(updated);
    } catch (e) {
      const msg = e?.response?.data?.detail || (lang === "bg" ? "Грешка при преизчисление" : "Recompute failed");
      toast.error(msg, { id: "recompute" });
    } finally {
      setRecomputing(false);
    }
  };

  // iter479 — Add meal to food diary → navigate to Diary's Plate Simulator anchor
  const sendToPlateSim = async (meal) => {
    try {
      const items = (meal.foods || []).map((f) => {
        const amt = Number(f.amount_g) || 100;
        const m = estimateMacrosPer100g(f.food);
        const factor = amt / 100;
        return {
          meal_type: meal.type || "snack",
          food_name: f.food,
          amount_g: amt,
          calories: +(m.kcal * factor).toFixed(1),
          protein:  +(m.p * factor).toFixed(2),
          carbs:    +(m.c * factor).toFixed(2),
          fat:      +(m.f * factor).toFixed(2),
          fiber:    +(m.fi * factor).toFixed(2),
        };
      });
      if (items.length === 0) {
        toast.error(lang === "bg" ? "Няма съставки за добавяне" : "No ingredients to add");
        return;
      }
      toast.loading(lang === "bg" ? "Добавям в дневника…" : "Adding to diary…", { id: "send-plate" });
      const res = await axios.post(`${API}/meals/add-batch`, { items });
      const added = res.data?.added_count ?? items.length;
      toast.success(
        lang === "bg" ? `${added} съставки добавени в дневника` : `${added} ingredients added to diary`,
        { id: "send-plate" }
      );
      onClose?.();
      // Navigate to diary with deep-link to plate simulator section
      navigate("/dashboard/diary?focus=plate-simulator#plate-simulator-anchor");
    } catch (e) {
      toast.error(
        lang === "bg" ? "Грешка при добавяне в дневника" : "Failed to add to diary",
        { id: "send-plate" }
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={`relative w-full max-w-5xl max-h-[92vh] flex flex-col rounded-2xl shadow-2xl ${isDark ? "bg-slate-900 text-white" : "bg-white text-slate-900"}`}
        onClick={(e) => e.stopPropagation()}
        data-testid="ai-menu-modal"
      >
        <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-slate-500/20" aria-label="Close">
          <X className="w-5 h-5" />
        </button>

        <div className="px-6 pt-6 pb-3 shrink-0 pr-12">
          <div className="flex items-center gap-2 mb-1">
            <ChefHat className="w-5 h-5 text-pink-500" />
            <h2 className="text-lg font-bold">{title}</h2>
          </div>
          {(menu?.weekly_summary_bg || menu?.weekly_summary_en) && (
            <p className={`text-xs mt-2 leading-relaxed ${isDark ? "text-slate-300" : "text-slate-600"}`}>
              {lang === "bg" ? menu.weekly_summary_bg : (menu.weekly_summary_en || menu.weekly_summary_bg)}
            </p>
          )}
          {/* iter482 — Personalized badge — shows which profile params are applied */}
          {profileApplied && (profileApplied.phenotype || profileApplied.homa_ir || profileApplied.pcos || (profileApplied.medications && profileApplied.medications.length > 0)) && (
            <div
              data-testid="menu-tuned-badge"
              className={`mt-3 inline-flex items-center gap-1.5 text-[10.5px] font-semibold px-2.5 py-1 rounded-full border ${
                isDark ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-emerald-300 bg-emerald-50 text-emerald-700"
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              {lang === "bg" ? "Tuned за:" : "Tuned for:"}
              {profileApplied.phenotype && <span className="font-normal opacity-90"> {profileApplied.phenotype}</span>}
              {profileApplied.homa_ir != null && <span className="font-normal opacity-90"> · HOMA-IR {profileApplied.homa_ir}</span>}
              {profileApplied.pcos && <span className="font-normal opacity-90"> · PCOS</span>}
              {profileApplied.medications && profileApplied.medications.length > 0 && (
                <span className="font-normal opacity-90"> · {profileApplied.medications.length} {lang === "bg" ? "медикам." : "meds"}</span>
              )}
              {profileApplied.biological_sex && <span className="font-normal opacity-60"> · {profileApplied.biological_sex === "female" ? (lang === "bg" ? "♀" : "F") : (lang === "bg" ? "♂" : "M")}</span>}
            </div>
          )}
          {/* iter481 — Recompute with personal profile button */}
          {menuId && (
            <button
              data-testid="menu-recompute-with-profile"
              onClick={recomputeWithProfile}
              disabled={recomputing}
              className={`mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg border transition-all ${
                isDark
                  ? "border-violet-500/40 text-violet-200 hover:bg-violet-500/15 disabled:opacity-50"
                  : "border-violet-300/70 text-violet-700 hover:bg-violet-100/70 disabled:opacity-50"
              }`}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${recomputing ? "animate-spin" : ""}`} />
              {lang === "bg"
                ? (recomputing ? "Преизчислявам…" : "🔄 Преизчисли с твоите профилни параметри")
                : (recomputing ? "Recomputing…" : "🔄 Recompute with your profile")}
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-4">
          {days.map((d, di) => (
            <section
              key={di}
              data-testid={`menu-day-${d.day || di + 1}`}
              className={`rounded-xl border p-3 ${isDark ? "bg-slate-800/40 border-slate-700" : "bg-slate-50 border-slate-200"}`}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-pink-500 text-white text-[11px] font-bold">
                    {d.day || di + 1}
                  </span>
                  {d.label}
                </h3>
                {d.totals && (
                  <div className="flex gap-1.5 text-[10px] font-mono flex-wrap">
                    <span className="px-2 py-0.5 rounded bg-orange-500/15 text-orange-600">DIL {d.totals.dil_g ?? "—"}g</span>
                    <span className="px-2 py-0.5 rounded bg-violet-500/15 text-violet-600">NIS {d.totals.nis_pct ?? "—"}%</span>
                    <span className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-600">COL3 {d.totals.col3 ?? "—"}</span>
                    {d.totals.calories != null && (
                      <span className={`px-2 py-0.5 rounded ${isDark ? "bg-slate-700 text-slate-300" : "bg-slate-200 text-slate-700"}`}>
                        {d.totals.calories} kcal
                      </span>
                    )}
                    {d.totals.pral != null && (
                      <span className={`px-2 py-0.5 rounded ${d.totals.pral < 0 ? "bg-teal-500/15 text-teal-600" : "bg-rose-500/15 text-rose-600"}`}>
                        PRAL {d.totals.pral > 0 ? "+" : ""}{d.totals.pral}
                      </span>
                    )}
                    {d.totals.calorie_density != null && (
                      <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-600">
                        CD {d.totals.calorie_density} kcal/g
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                {(d.meals || []).map((m, mi) => {
                  const tone = {
                    breakfast: "from-amber-400 to-orange-500",
                    lunch:     "from-emerald-400 to-teal-500",
                    dinner:    "from-violet-400 to-fuchsia-500",
                    snack:     "from-sky-400 to-cyan-500",
                  }[m.type] || "from-slate-400 to-slate-500";
                  const typeLabel = t[m.type] || m.type;
                  return (
                    <div
                      key={mi}
                      data-testid={`menu-meal-${di}-${mi}`}
                      className={`rounded-lg p-2.5 ${isDark ? "bg-slate-900/60" : "bg-white"} border ${isDark ? "border-slate-700" : "border-slate-200"}`}
                    >
                      <div className="flex items-center justify-between mb-1.5 gap-2">
                        <div className="flex items-center gap-1.5 flex-1 min-w-0">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded text-white bg-gradient-to-r ${tone}`}>
                            {typeLabel}
                          </span>
                          <h4 className="text-xs font-semibold truncate">{m.name}</h4>
                        </div>
                      </div>
                      <ul className={`text-[11px] mb-1.5 space-y-0.5 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                        {(m.foods || []).map((f, fi) => (
                          <li key={fi} className="flex justify-between gap-2">
                            <span className="truncate">• {f.food}</span>
                            <span className="font-mono shrink-0 opacity-60">{f.amount_g}g</span>
                          </li>
                        ))}
                      </ul>
                      {m.estimates && (
                        <div className="flex gap-1 text-[9px] font-mono flex-wrap">
                          <span className="px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-600">DIL {m.estimates.dil_g ?? "—"}</span>
                          <span className="px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-600">NIS {m.estimates.nis_pct ?? "—"}</span>
                          <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600">COL3 {m.estimates.col3 ?? "—"}</span>
                          {m.estimates.calories != null && (
                            <span className={`px-1.5 py-0.5 rounded ${isDark ? "bg-slate-700 text-slate-300" : "bg-slate-200 text-slate-700"}`}>{m.estimates.calories} kcal</span>
                          )}
                          {m.estimates.pral != null && (
                            <span className={`px-1.5 py-0.5 rounded ${m.estimates.pral < 0 ? "bg-teal-500/10 text-teal-600" : "bg-rose-500/10 text-rose-600"}`}>
                              PRAL {m.estimates.pral > 0 ? "+" : ""}{m.estimates.pral}
                            </span>
                          )}
                          {m.estimates.calorie_density != null && (
                            <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600">CD {m.estimates.calorie_density}</span>
                          )}
                        </div>
                      )}
                      {m.rationale_bg && (
                        <p className={`mt-1.5 text-[10px] leading-relaxed italic ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                          {m.rationale_bg}
                        </p>
                      )}
                      <button
                        data-testid={`menu-send-plate-${di}-${mi}`}
                        onClick={() => sendToPlateSim(m)}
                        className={`mt-2 w-full inline-flex items-center justify-center gap-1 text-[10px] font-semibold py-1.5 rounded-md border transition-colors ${
                          isDark
                            ? "border-pink-500/40 text-pink-300 hover:bg-pink-500/15"
                            : "border-pink-400/60 text-pink-700 hover:bg-pink-50"
                        }`}
                      >
                        {lang === "bg" ? "+ Добави в дневника → Симулатор" : "+ Add to diary → Simulator"}
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
          <p className="text-[10px] opacity-60 text-center pt-2">{menu?.disclaimer_bg || disclaimer}</p>
        </div>
      </div>
    </div>
  );
}
